// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import translate from "@insite/client-framework/Translate";
import { StyledButton } from "@insite/mobius/Clickable";
import Clickable from "@insite/mobius/Clickable/Clickable";
import Img, { ImgProps } from "@insite/mobius/Img";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { getProp, injectCss } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { color } from "@west/XDL/Utilities";
import React, { FC } from "react";
import styled from "styled-components";

export const DEFAULT_CONTENT_BLOCK_WIDTH = 360;

export interface ContentBlockModel {
    url?: string;
    title?: string;
    image?: string;
    openInNewWindow?: boolean;
}

export interface ContentBlockProps {
    contentBlock: ContentBlockModel;
    menuWidth: number;
    shiftOffset: number;
    viewAllLink: JSX.Element;
}

const ContentBlockElement = styled.div<{ width: number; shift: number }>`
    ${injectCss}
`;

interface ContentBlockStyles {
    container?: InjectableCss;
    title?: TypographyPresentationProps;
    link?: LinkPresentationProps;
    image?: ImgProps;
}

export const contentBlockStyles: ContentBlockStyles = {};

const styles = contentBlockStyles;
const ContentBlock: FC<ContentBlockProps> = ({ contentBlock, viewAllLink, menuWidth, shiftOffset }) => {
    return (
        <ContentBlockElement
            width={DEFAULT_CONTENT_BLOCK_WIDTH}
            shift={menuWidth * shiftOffset}
            className="contentBlock"
            {...styles.container}
        >
            <Typography size={16} weight="bold" {...styles.title}>
                {contentBlock.title}
            </Typography>
            {viewAllLink}
            {contentBlock.image && (
                <Clickable
                    href={contentBlock.url}
                    target={contentBlock.url && contentBlock.openInNewWindow ? "_blank" : ""}
                    {...styles.link}
                >
                    <Img
                        src={contentBlock.image}
                        alt={contentBlock.title || translate("Content Block Image")}
                        {...styles.image}
                    />
                </Clickable>
            )}
        </ContentBlockElement>
    );
};

export default ContentBlock;
